import { useState, useEffect, useRef, useCallback } from 'react';
import fetchCheck from '../utils/drugInteraction/fetchCheck';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const pollDrugInteractions = async (uuid) => {
  const startTime = Date.now();
  const timeout = 300000; // 5 minutes timeout

  while (Date.now() - startTime <= timeout) {
    const drugInteractions = await fetchCheck(uuid);
    if (drugInteractions.isErrored()) return drugInteractions; // Stop polling if errored, but do not treat as ready
    if (drugInteractions.isReady()) return drugInteractions;
    await sleep(2000);
  }

  console.error('[useDrugInteractionsPolling] Polling timeout reached');
  throw new Error('Drug interactions polling timeout');
};

export default function useDrugInteractionsPolling() {
  const isMounted = useRef(true);
  const [drugInteractions, setDrugInteractions] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const startPolling = async (uuid) => {
    if (!uuid) {
      return;
    }

    setIsPolling(true);
    setError(null);
    
    try {
      const results = await pollDrugInteractions(uuid);
      if (!isMounted.current) {
        return; // Stop if component unmounted
      }
      
      setDrugInteractions(results);
    } catch (error) {
      if (!isMounted.current) {
        return; // Stop if component unmounted
      }
      console.error('[useDrugInteractionsPolling] Polling failed:', error);
      setError(error);
    } finally {
      if (isMounted.current) {
        setIsPolling(false);
      }
    }
  };

  const clearResults = useCallback(() => {
    setDrugInteractions(null);
    setError(null);
  }, []);

  return { drugInteractions, isPolling, error, startPolling, clearResults };
} 